import React, { useState } from "react";
import { FaMicrophone, FaRegFileAlt, FaMagic } from "react-icons/fa";
import AdminModal from "../AdminModal";
import { colors } from "../../utils/designTokens";
import { HABI_API_BASE_URL, getAuthHeaders } from "../../utils/habiApi";
import { mergeAiActa, padronPayload } from "../../utils/actaAi";
import { TIPOS_ASAMBLEA, CONVOCATORIAS } from "../../utils/actaConfig";

// Asistente IA del Editor de Actas. Modal con dos modos: subir audio (Whisper) o
// pegar transcripción. Llama al backend (propia_agent), recibe el acta en JSON y
// la mergea con el padrón al estado del editor (asistencia, orden del día y votos).
//
// Props:
//   open, onClose
//   buildingId       - id del edificio seleccionado
//   padron           - { edificio, copropietarios }
//   acta             - acta base actual (se respeta id/edificio)
//   onApply(acta, stats) - aplica el acta mergeada

const MAX_AUDIO_MB = 25;

export default function ActaAiAssistant({ open, onClose, buildingId, padron, acta, onApply, showToast }) {
  const [mode, setMode] = useState("audio");
  const [audio, setAudio] = useState(null);
  const [texto, setTexto] = useState("");
  const [meetingNumber, setMeetingNumber] = useState("");
  const [tipo, setTipo] = useState("ordinaria");
  const [convocatoria, setConvocatoria] = useState("segunda");
  const [fecha, setFecha] = useState("");
  const [processing, setProcessing] = useState(false);

  const reset = () => {
    setAudio(null);
    setTexto("");
    setProcessing(false);
  };

  const notify = (msg, type) => (showToast ? showToast(msg, type) : null);

  const applyResult = (data) => {
    const aiActa = data?.acta || {};
    if (!aiActa || (!aiActa.ordenDelDia && !aiActa.asistencia)) {
      notify("La IA no devolvió un acta utilizable. Probá con más detalle.", "warning");
      return;
    }
    const { acta: merged, stats } = mergeAiActa(aiActa, padron, acta);
    onApply(merged, stats);
    const warnParts = [];
    if (stats.personasNoMapeadas?.length) warnParts.push(`${stats.personasNoMapeadas.length} persona(s) sin mapear`);
    if (stats.unidadesNoMapeadas?.length) warnParts.push(`${stats.unidadesNoMapeadas.length} unidad(es) sin mapear`);
    notify(
      `Acta generada (${stats.puntos} puntos)${warnParts.length ? ` · revisá: ${warnParts.join(", ")}` : ""}.`,
      warnParts.length ? "warning" : "success"
    );
    reset();
    onClose();
  };

  const processAudio = async () => {
    if (!audio) return notify("Subí un archivo de audio.", "warning");
    if (audio.size > MAX_AUDIO_MB * 1024 * 1024) {
      return notify(`El audio supera ${MAX_AUDIO_MB}MB. Dividilo o pegá la transcripción.`, "warning");
    }
    setProcessing(true);
    try {
      const headers = await getAuthHeaders();
      delete headers["Content-Type"]; // FormData setea el boundary
      const { units, copropietarios } = padronPayload(padron);
      const fd = new FormData();
      fd.append("audio", audio);
      fd.append("building_id", String(buildingId));
      fd.append("units", JSON.stringify(units));
      fd.append("copropietarios", JSON.stringify(copropietarios));
      if (meetingNumber) fd.append("meeting_number", meetingNumber);
      fd.append("meeting_type", tipo);
      fd.append("convocatoria", convocatoria);
      if (fecha) fd.append("fecha", fecha);

      const resp = await fetch(`${HABI_API_BASE_URL}/api/process-meeting-acta-audio`, {
        method: "POST",
        headers,
        body: fd,
      });
      if (!resp.ok) {
        const err = await resp.json().catch(() => ({}));
        return notify(err.detail || "No se pudo procesar el audio.", "error");
      }
      applyResult(await resp.json());
    } catch (e) {
      notify(e.message || "Error inesperado procesando el audio.", "error");
    } finally {
      setProcessing(false);
    }
  };

  const processText = async () => {
    if (!texto.trim()) return notify("Pegá la transcripción o describí la asamblea.", "warning");
    setProcessing(true);
    try {
      const headers = await getAuthHeaders();
      const { units, copropietarios } = padronPayload(padron);
      const resp = await fetch(`${HABI_API_BASE_URL}/api/structure-meeting-acta`, {
        method: "POST",
        headers,
        body: JSON.stringify({
          building_id: String(buildingId),
          transcription_text: texto,
          units,
          copropietarios,
          meeting_number: meetingNumber || null,
          meeting_type: tipo,
          convocatoria,
          fecha: fecha || null,
        }),
      });
      if (!resp.ok) {
        const err = await resp.json().catch(() => ({}));
        return notify(err.detail || "No se pudo estructurar la transcripción.", "error");
      }
      applyResult(await resp.json());
    } catch (e) {
      notify(e.message || "Error inesperado.", "error");
    } finally {
      setProcessing(false);
    }
  };

  const onSubmit = mode === "audio" ? processAudio : processText;
  const canSubmit = mode === "audio" ? !!audio : !!texto.trim();

  return (
    <AdminModal
      open={open}
      onClose={processing ? () => {} : onClose}
      title="Asistente IA · armar acta"
      subtitle="Subí el audio de la asamblea o pegá la transcripción. La IA completa asistencia, orden del día, resoluciones y votos."
      size="medium"
      icon={<FaMagic />}
      footer={
        <div style={{ display: "flex", gap: 8, justifyContent: "flex-end", width: "100%" }}>
          <button type="button" onClick={onClose} disabled={processing} style={S.btnGhost}>Cancelar</button>
          <button type="button" onClick={onSubmit} disabled={processing || !canSubmit} style={{ ...S.btnPrimary, opacity: processing || !canSubmit ? 0.6 : 1 }}>
            {processing ? "Generando…" : "Generar acta"}
          </button>
        </div>
      }
    >
      <div style={S.tabs}>
        <button type="button" onClick={() => setMode("audio")} style={{ ...S.tab, ...(mode === "audio" ? S.tabActive : {}) }}>
          <FaMicrophone size={13} /> Subir audio
        </button>
        <button type="button" onClick={() => setMode("text")} style={{ ...S.tab, ...(mode === "text" ? S.tabActive : {}) }}>
          <FaRegFileAlt size={13} /> Pegar texto
        </button>
      </div>

      <div style={S.contextGrid}>
        <Field label="N° de acta">
          <input type="text" value={meetingNumber} onChange={(e) => setMeetingNumber(e.target.value)} placeholder="2026-01" style={S.input} />
        </Field>
        <Field label="Tipo">
          <select value={tipo} onChange={(e) => setTipo(e.target.value)} style={S.input}>
            {TIPOS_ASAMBLEA.map((o) => <option key={o.value} value={o.value}>{o.label}</option>)}
          </select>
        </Field>
        <Field label="Convocatoria">
          <select value={convocatoria} onChange={(e) => setConvocatoria(e.target.value)} style={S.input}>
            {CONVOCATORIAS.map((o) => <option key={o.value} value={o.value}>{o.label}</option>)}
          </select>
        </Field>
        <Field label="Fecha">
          <input type="date" value={fecha} onChange={(e) => setFecha(e.target.value)} style={S.input} />
        </Field>
      </div>

      {mode === "audio" ? (
        <div>
          <label style={S.label}>Audio de la asamblea (mp3, m4a, wav · máx. {MAX_AUDIO_MB}MB · ~30 min)</label>
          <input type="file" accept="audio/*" onChange={(e) => setAudio(e.target.files?.[0] || null)} style={{ width: "100%", fontSize: 13 }} />
          {audio && (
            <div style={S.fileMeta}>{audio.name} · {(audio.size / 1024 / 1024).toFixed(1)} MB</div>
          )}
        </div>
      ) : (
        <div>
          <label style={S.label}>Transcripción o descripción de la asamblea</label>
          <textarea
            value={texto}
            onChange={(e) => setTexto(e.target.value)}
            rows={9}
            placeholder={"La asamblea inició a las 19:30. Presentes: unidad 102 (María Acuña), 201 (Juan Bermúdez)... Se aprobó la rendición de cuentas por mayoría, con la abstención de la unidad 202..."}
            style={{ ...S.input, resize: "vertical", lineHeight: 1.5 }}
          />
        </div>
      )}

      <p style={S.disclaimer}>
        ⚠ El acta se genera con asistencia de IA y precarga el editor. Revisá asistencia, votos y mayorías antes de firmar.
        La responsabilidad legal recae en la administración y la mesa de la asamblea.
      </p>
    </AdminModal>
  );
}

function Field({ label, children }) {
  return (
    <label style={{ display: "flex", flexDirection: "column", gap: 4 }}>
      <span style={S.label}>{label}</span>
      {children}
    </label>
  );
}

const S = {
  tabs: { display: "flex", gap: 8, marginBottom: 16 },
  tab: {
    display: "inline-flex",
    alignItems: "center",
    gap: 6,
    padding: "8px 14px",
    fontSize: 13,
    color: colors.primary,
    background: colors.primarySubtle,
    border: `1px solid ${colors.primaryLighter || colors.primarySubtle}`,
    borderRadius: 8,
    cursor: "pointer",
    fontFamily: "inherit",
  },
  tabActive: { color: colors.white, background: colors.primary, borderColor: colors.primary },
  contextGrid: { display: "grid", gridTemplateColumns: "1fr 1fr 1fr 1fr", gap: 10, marginBottom: 16 },
  label: { fontSize: 11, color: colors.gray700, fontWeight: 400, marginBottom: 4, display: "block" },
  input: {
    width: "100%",
    border: `1px solid ${colors.gray300}`,
    borderRadius: 6,
    padding: "8px 10px",
    fontSize: 13,
    color: colors.black,
    fontFamily: "inherit",
    outline: "none",
    background: colors.white,
  },
  fileMeta: { fontSize: 12, color: colors.gray600, marginTop: 6 },
  btnGhost: {
    background: colors.white,
    color: colors.gray700,
    border: `1px solid ${colors.gray300}`,
    borderRadius: 8,
    padding: "9px 14px",
    fontSize: 13,
    cursor: "pointer",
    fontFamily: "inherit",
  },
  btnPrimary: {
    background: colors.primary,
    color: colors.white,
    border: 0,
    borderRadius: 8,
    padding: "9px 16px",
    fontSize: 13,
    cursor: "pointer",
    fontFamily: "inherit",
  },
  disclaimer: { marginTop: 16, fontSize: 11, color: colors.gray600, lineHeight: 1.5, fontStyle: "italic" },
};
